import { mediaList, type MediaItem, type MediaType } from '@/mocks/movies';

export const typeLabels: Record<MediaType, string> = {
  movie: '电影',
  tv: '电视剧',
  variety: '综艺',
};

export const categoryMeta: Record<MediaType, { title: string; desc: string; icon: string }> = {
  movie: {
    title: '电影精选',
    desc: '汇聚国内外院线大片与高分佳作，涵盖动作、科幻、剧情、悬疑等多种类型。',
    icon: 'ri-film-line',
  },
  tv: {
    title: '电视剧精选',
    desc: '精选热播剧集与经典长篇，从都市情感到历史传奇，陪你追完每一集。',
    icon: 'ri-tv-2-line',
  },
  variety: {
    title: '综艺精选',
    desc: '轻松解压的综艺大集合，真人秀、脱口秀、旅行美食应有尽有。',
    icon: 'ri-mic-line',
  },
};

export function typeLabel(type: MediaType): string {
  return typeLabels[type] ?? '影视';
}

export function categoryPath(type: MediaType): string {
  return `/category/${type}`;
}

export function getItemById(id: string): MediaItem | undefined {
  return mediaList.find((item) => item.id === id);
}

export function getItemsByType(type: MediaType): MediaItem[] {
  return mediaList.filter((item) => item.type === type);
}

export function getRelated(item: MediaItem, limit = 5): MediaItem[] {
  const sameType = mediaList.filter((i) => i.id !== item.id && i.type === item.type);
  const sharedGenre = sameType.filter((i) => i.genres.some((g) => item.genres.includes(g)));
  const rest = sameType.filter((i) => !sharedGenre.includes(i));
  return [...sharedGenre, ...rest].slice(0, limit);
}

export function getFeatured(): MediaItem[] {
  return mediaList.filter((item) => item.featured);
}

export function getLatest(limit = 12): MediaItem[] {
  return [...mediaList].sort((a, b) => b.year - a.year || b.heat - a.heat).slice(0, limit);
}

export function getHotRanking(limit = 8): MediaItem[] {
  return [...mediaList].sort((a, b) => b.heat - a.heat).slice(0, limit);
}

export interface FilterState {
  region: string;
  year: string;
  sort: string;
}

export function filterByType(type: MediaType, filter: FilterState): MediaItem[] {
  let list = getItemsByType(type);
  if (filter.region && filter.region !== '全部') {
    list = list.filter((item) => item.region === filter.region);
  }
  if (filter.year && filter.year !== '全部') {
    list = list.filter((item) => String(item.year) === filter.year);
  }
  if (filter.sort === '最新') {
    list = [...list].sort((a, b) => b.year - a.year);
  } else if (filter.sort === '热门') {
    list = [...list].sort((a, b) => b.heat - a.heat);
  } else if (filter.sort === '评分') {
    list = [...list].sort((a, b) => b.rating - a.rating);
  }
  return list;
}

export function searchMedia(keyword: string, type?: MediaType | 'all'): MediaItem[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  let list = mediaList.filter((item) => {
    const haystack = [
      item.title,
      item.director,
      item.region,
      typeLabel(item.type),
      ...item.genres,
      ...item.cast,
      item.synopsis,
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(kw);
  });
  if (type && type !== 'all') {
    list = list.filter((item) => item.type === type);
  }
  return list.sort((a, b) => b.heat - a.heat);
}

export function formatHeat(heat: number): string {
  if (heat >= 10000) return `${(heat / 10000).toFixed(1)} 万`;
  return `${heat}`;
}